/**
 * Merko Integrations — pending connection attempts.
 *
 * The handoff requires the attempt to be held "in browser session state scoped
 * to the signed-in Merko session", and the callback page to compare the
 * callback's `attempt_id` against the stored one before completing. A redirect
 * that merely loaded proves nothing; this store is what turns it into proof.
 *
 * Three deliberate choices:
 *
 *   • **`sessionStorage`, not `localStorage`.** An attempt is valid for ten
 *     minutes within one session. Surviving a browser restart would only widen
 *     the window in which a stale attempt could be completed.
 *   • **`redirect_url` is never stored.** The handoff forbids putting it in
 *     logs or analytics, and the least error-prone way to honour that is for
 *     the app never to hold a copy once it has been opened.
 *   • **Scoped by user key.** Two Merko users sharing a browser profile must
 *     not see each other's attempts; the stored record carries the user it
 *     belongs to and is rejected on mismatch.
 */

import { ATTEMPT_TTL_MS, isKnownAppId } from "./types";
import type { AppId } from "./types";

const KEY = "merko.integrations.attempt";

export interface StoredAttempt {
    attemptId: string;
    appId: AppId;
    /** Unix **milliseconds**, normalised on write. */
    expiresAtMs: number;
    /** Identifies the Merko user this attempt belongs to. */
    userKey: string;
    startedAtMs: number;
}

/** `expires_at` is documented in seconds; tolerate either unit. */
function toMillis(expiresAt: number): number {
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return Date.now() + ATTEMPT_TTL_MS;
    // Anything this small cannot be milliseconds since the epoch.
    return expiresAt < 1e11 ? expiresAt * 1000 : expiresAt;
}

function storage(): Storage | null {
    try {
        return typeof sessionStorage !== "undefined" ? sessionStorage : null;
    } catch {
        // Blocked site data, or a privacy mode that throws on access.
        return null;
    }
}

export function saveAttempt(attempt: {
    attemptId: string;
    appId: AppId;
    expiresAt: number;
    userKey: string;
}): void {
    const store = storage();
    if (!store) return;
    const record: StoredAttempt = {
        attemptId: attempt.attemptId,
        appId: attempt.appId,
        expiresAtMs: toMillis(attempt.expiresAt),
        userKey: attempt.userKey,
        startedAtMs: Date.now(),
    };
    try {
        store.setItem(KEY, JSON.stringify(record));
    } catch {
        // Nothing to do: the callback will simply fail the comparison and the
        // user starts again, which is the safe direction to fail in.
    }
}

/**
 * The stored attempt, if it is still usable by this user.
 *
 * Returns null for an expired attempt or a different user, so a caller cannot
 * accidentally complete something it should have discarded.
 */
export function readAttempt(userKey: string): StoredAttempt | null {
    const store = storage();
    if (!store) return null;

    let record: StoredAttempt | null = null;
    try {
        const raw = store.getItem(KEY);
        record = raw ? (JSON.parse(raw) as StoredAttempt) : null;
    } catch {
        return null;
    }

    if (!record?.attemptId || !isKnownAppId(record.appId)) return null;
    if (record.userKey !== userKey) return null;
    if (record.expiresAtMs <= Date.now()) return null;
    return record;
}

export function clearAttempt(): void {
    try {
        storage()?.removeItem(KEY);
    } catch {
        /* nothing to clear */
    }
}

export type AttemptMatch =
    | { ok: true; attempt: StoredAttempt }
    | { ok: false; reason: "missing" | "mismatch" | "expired" };

/**
 * Decide which attempt a callback belongs to.
 *
 * What actually arrives on the URL was checked against Composio's own
 * documentation: after the provider callback it redirects the browser to the
 * project's verifier URL with **one** query parameter, `session_uri`, which
 * "carries no connection id, user id, or toolkit name". So on the OAuth path
 * there is no `attempt_id` in the query string for this page to read — the
 * attempt is known only from this session's stored state, which is exactly why
 * the handoff has it saved there before the redirect.
 *
 * Hence two cases:
 *
 *   • The URL **does** carry an `attempt_id` (a Merko-built callback URL may
 *     add one): it must equal the stored attempt, or the flow stops.
 *   • The URL carries **none**: the stored attempt for this user is the one.
 *
 * Neither case trusts the browser for identity. The attempt ID in the request
 * path always comes from this user's own session state, and the server makes
 * the real decision — it completes the session against the signed-in user and
 * refuses a mismatch (`account_mismatch`).
 *
 * `mismatch` and `expired` are kept apart because they call for different
 * copy — one is a wrong-tab or replay situation, the other simply took too
 * long — and the handoff treats `expired_attempt` as its own error state.
 */
export function matchAttempt(userKey: string, callbackAttemptId: string | null): AttemptMatch {
    const store = storage();
    let record: StoredAttempt | null = null;
    if (store) {
        try {
            const raw = store.getItem(KEY);
            record = raw ? (JSON.parse(raw) as StoredAttempt) : null;
        } catch {
            record = null;
        }
    }

    if (!record?.attemptId || record.userKey !== userKey) return { ok: false, reason: "missing" };
    if (callbackAttemptId && callbackAttemptId !== record.attemptId) {
        return { ok: false, reason: "mismatch" };
    }
    if (record.expiresAtMs <= Date.now()) return { ok: false, reason: "expired" };
    return { ok: true, attempt: record };
}

/** Milliseconds left on an attempt, floored at zero. */
export const attemptTimeLeftMs = (attempt: StoredAttempt): number =>
    Math.max(0, attempt.expiresAtMs - Date.now());
